import React, { useState, useEffect, useCallback } from 'react';
import BlogList from '../components/blog/BlogList';
import BlogSearch from '../components/blog/BlogSearch';
import CategoryFilter from '../components/blog/CategoryFilter';
import TagFilter from '../components/blog/TagFilter';
import NewsletterSignup from '../components/NewsletterSignup';
import SEO from '../components/SEO';
import AdSenseLoader from '../components/ads/AdSenseLoader';
import PageHero from '../components/PageHero';
import DisplayAd from '../components/ads/DisplayAd';
import { SITE_URL } from '../config/site';
import { 
  getAllBlogPosts, 
  searchBlogPosts, 
  getBlogPostsByCategory,
  getAllCategories,
  getAllTags
} from '../services/blogService';
import './BlogPage.css';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load data - functions now return empty arrays on error instead of throwing
        const postsData = await getAllBlogPosts();
        const categoriesData = await getAllCategories();
        const tagsData = await getAllTags();
        
        setPosts(postsData || []);
        setCategories(categoriesData || []);
        setTags(tagsData || []);
        
        // Empty collection is not an error - it's just that no posts exist yet
        setError(null);
        
        if (postsData.length === 0) {
          console.log('No blog posts found. This is normal if you haven\'t added any posts yet.');
          console.log('To add posts: Go to Firebase Console → Firestore → Create collection "blogPosts" and add documents.');
          console.log('See SEED_BLOG_POST.md or TROUBLESHOOTING.md for instructions.');
        }
      } catch (err) {
        console.error('Unexpected error loading blog data:', err);
        // Only show error for unexpected critical errors
        // Since getAllBlogPosts returns [] on error, this catch shouldn't normally trigger
        setError(null); // Don't show error - empty state is handled by BlogList
        setPosts([]);
        setCategories([]);
        setTags([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter posts based on search, category, and tags
  useEffect(() => {
    const filterPosts = async () => {
      if (loading) {
        setFilteredPosts([]);
        return;
      }

      let result = [...posts];

      // Apply search filter first
      if (searchTerm.trim()) {
        try {
          result = await searchBlogPosts(searchTerm);
        } catch (err) {
          console.error('Error searching posts:', err);
          result = posts.filter(post => 
            post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            post.content?.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
      }

      // Apply category filter
      if (selectedCategory) {
        result = result.filter(post => post.category === selectedCategory);
      }

      // Apply tag filters (posts must have ALL selected tags)
      if (selectedTags.length > 0) {
        result = result.filter(post => 
          selectedTags.every(tag => post.tags?.includes(tag))
        );
      }

      setFilteredPosts(result);
    };

    filterPosts();
  }, [posts, searchTerm, selectedCategory, selectedTags, loading]);

  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  return (
    <div className="blog-page">
      <AdSenseLoader />
      <SEO 
        title="Blog - Dental Care Tips & Insights | Dr. Muyabin"
        description="Stay informed with the latest dental care tips, orthodontics insights, and oral health advice from Dr. Muyabin."
        keywords="dental care tips, oral health, orthodontics blog, dental hygiene, Tanzania dentist blog"
        url={`${SITE_URL}/blog`}
      />
      <PageHero
        title="Our Blog"
        subtitle="Stay informed with the latest dental care tips and insights"
        breadcrumb={[
          { label: 'Home', href: '/' },
          { label: 'Blog' },
        ]}
      />
      <div className="blog-page-container">
        <DisplayAd />

        <div className="blog-page-filters">
          <BlogSearch onSearch={handleSearch} />
          
          <div className="blog-page-filters-row">
            <CategoryFilter
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
            />
            
            <TagFilter
              tags={tags}
              selectedTags={selectedTags}
              onTagToggle={handleTagToggle}
            />
          </div>
        </div>

        <BlogList 
          posts={filteredPosts} 
          loading={loading}
          error={error}
        />

        <div style={{ marginTop: '60px' }}>
          <NewsletterSignup variant="compact" />
        </div>
      </div>
    </div>
  );
};

export default BlogPage;

